// La porte unique par laquelle passent toutes les requêtes de `supabase-js`.
//
// POURQUOI CE MODULE EXISTE
// -------------------------
// Vérifié dans `node_modules/@supabase/supabase-js/dist/index.mjs` :
//
//   async _getAccessToken() {
//     const { data } = await this.auth.getSession();
//     return data.session?.access_token ?? this.supabaseKey;   // ← le piège
//   }
//
// Quand `getSession()` ne rend aucune session, la bibliothèque ne s'arrête pas :
// elle tamponne la requête avec la clé publique (`anon`) et l'envoie quand même.
// PostgREST l'exécute en rôle `anon`, la RLS refuse, Postgres écrit `42501` et
// PostgREST rend 401. À l'écran il ne se passe rien de compréhensible : la
// personne ne conclut pas « je suis déconnectée », elle conclut « mes
// rendez-vous ont disparu ».
//
// Mesuré en production le 2026-08-23 : 192 erreurs entre 11:04 et 11:06, puis
// 73 à 18:50, correspondant une pour une aux 401 vus côté edge.
//
// Le correctif du 5 août rattrape ces 401 APRÈS coup, et garde 8 points d'appel
// sur les 189 du dépôt. Ici on ferme le piège à l'étage du transport : un seul
// endroit, tous les appels, y compris ceux qui n'existent pas encore.
//
// Depuis le 2026-09-26, la porte regarde aussi le RETOUR : un 401 revenu sur
// une requête partie avec le jeton d'une personne déclenche un renouvellement
// et UN rejeu (voir `sendWithPersonJwt`). Spec :
// `.superpowers/specs/2026-09-26-porte-401-renouveler-rejouer-design.md`.
//
// CE QUE CE MODULE NE FAIT PAS
// ----------------------------
// Il n'importe RIEN. Le client Supabase et le rattrapage d'erreurs lui sont
// injectés, pour deux raisons : le rendre testable sans navigateur, et éviter
// le cycle `client.ts` → porte → `authErrorRecovery.ts` → `client.ts`.

export interface SessionGuardDeps {
  /** La clé publique (`anon`). C'est elle que `supabase-js` tamponne en repli. */
  anonKey: string;
  /** Le `fetch` réel. Injecté pour les tests. */
  baseFetch: typeof fetch;
  /** L'app se croit-elle connectée ? (`sessionExpectation.ts`) */
  isSessionExpected: () => boolean;
  /**
   * La page courante peut-elle légitimement lire les APIs avec la clé anon ?
   * (`anonymousDataRoutes.ts`). Défaut : oui, le comportement d'avant pour
   * tout constructeur qui ne la fournit pas.
   */
  isAnonymousDataRoute?: () => boolean;
  /**
   * Une déconnexion volontaire est-elle en cours ? (`sessionExpectation.ts`).
   * Défaut : non, le comportement d'avant pour tout constructeur qui ne la
   * fournit pas.
   */
  isLogoutInProgress?: () => boolean;
  /** Renouvelle la session. Rend le nouveau JWT, ou `null` si c'est perdu. */
  refreshSession: () => Promise<string | null>;
  /** L'horloge du garde-fou, injectée pour les tests. Défaut : `Date.now`. */
  now?: () => number;
}

// Seules les requêtes de données sont concernées.
//
// ⛔ NE JAMAIS étendre à `/auth/v1/` : vérifié dans la bibliothèque
// (`this.auth = this._initSupabaseAuthClient(..., settings.global.fetch)`,
// index.mjs:218), le client d'authentification utilise CE fetch. L'intercepter
// ferait attendre à la porte un renouvellement qui attend la porte.
//
// `/functions/v1/` y est entré le 2026-09-12. Vérifié dans le SDK installé :
// `SupabaseClient.ts:197` passe `customFetch: this.fetch` au `FunctionsClient`,
// et `this.fetch` (ligne 160) est le même `fetchWithAuth` que les données. Un
// `functions.invoke` traversait donc DÉJÀ cette porte, tamponné par le même
// repli sur la clé anon, sans être filtré : 46 réponses 401 muettes sur
// `check-subscription-access` en 24 h, mesurées le 2026-09-12.
//
// `/storage/v1/` y est entré le 2026-09-26. Même câblage que les edges :
// `SupabaseClient.ts:179-182` passe ce même `this.fetch` au `StorageClient`,
// donc un téléversement dont le renouvellement de la bibliothèque a échoué
// partait tamponné anon et revenait en faux « droits refusés ».
//
// ⛔ Le serveur de fichiers ne répond JAMAIS 401 : un jeton expiré y rend un
// HTTP 400 portant `"statusCode":"403"`, exactement la forme d'un refus de
// politique (mesuré sur la pile locale le 2026-09-26). La réponse ne dit donc
// pas si la session est morte : seule la retenue au DÉPART s'applique aux
// fichiers, jamais le rejeu au retour (voir `RETURN_REPLAY_PATHS`).
const GUARDED_PATHS = ['/rest/v1/', '/functions/v1/', '/storage/v1/'] as const;

// Les chemins où un 401 revenu du serveur signifie un refus d'IDENTITÉ, donc
// renouvelable puis rejouable (spec du 2026-09-26, « renouveler puis
// rejouer »). `/storage/v1/` n'y est pas, pour la raison ci-dessus.
const RETURN_REPLAY_PATHS = ['/rest/v1/', '/functions/v1/'] as const;

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function readAuthorization(init?: RequestInit): string | null {
  if (!init?.headers) return null;
  try {
    return new Headers(init.headers).get('Authorization');
  } catch {
    // `null` fait passer la requête (voir plus bas) : c'est le sens sûr de
    // l'échec, pas une retenue par erreur. Inatteignable aujourd'hui,
    // `fetchWithAuth` fournit toujours une vraie instance `Headers`, mais si
    // ça change un jour, mieux vaut laisser filer que bloquer à tort.
    return null;
  }
}

/**
 * Le jeton d'une PERSONNE : `Bearer` suivi de trois segments non vides, et
 * différent de la clé anon (qui peut elle aussi avoir la forme d'un JWT).
 *
 * Deux exclusions voulues. Une requête anon est le tunnel `/book` : un 401 y
 * est une vraie réponse, pas une session à sauver. `Bearer undefined` vient
 * d'un écran qui pose l'en-tête lui même sur une session morte : il n'y a rien
 * à renouveler, et l'écran affiche déjà son propre message.
 */
function isPersonJwt(authorization: string | null, anonKey: string): boolean {
  if (!authorization?.startsWith('Bearer ')) return false;
  const token = authorization.slice('Bearer '.length);
  if (token === anonKey) return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

/**
 * Re-tamponne le jeton sans perdre les autres en-têtes (`apikey`, `Prefer`,
 * `Content-Type`…), que PostgREST exige, ni la méthode et le corps, qu'un
 * invoke exige.
 */
function restamp(init: RequestInit | undefined, jwt: string): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${jwt}`);
  return { ...init, headers };
}

/**
 * Pendant combien de temps une adresse qui a refusé une personne APRÈS un
 * renouvellement réussi ne déclenche plus de renouvellement. Sans ce délai,
 * un écran qui interroge en boucle une fonction qui refuse toujours cette
 * personne (contrôle d'identité à la 2FA, par exemple) renouvellerait la
 * session en boucle.
 */
export const REFUSAL_COOLDOWN_MS = 60_000;

/**
 * Pendant combien de temps le dernier jeton neuf obtenu par la porte sert
 * encore à rejouer un 401 TARDIF de la même rafale : une requête partie avec
 * l'ancien jeton, dont le refus arrive après la fin du renouvellement. Sans ce
 * délai, chacun de ces retardataires lancerait un renouvellement de plus
 * (rotation de jeton, `TOKEN_REFRESHED`, nouveau rendu d'`AuthContext`).
 */
export const FRESH_TOKEN_REUSE_MS = 10_000;

/**
 * La personne (`sub`) à qui appartient un jeton, lue dans sa charge utile.
 *
 * Pourquoi cette comparaison existe : un changement de compte rapide peut
 * laisser en route une requête partie au nom d'une personne alors que le
 * jeton neuf appartient déjà à une autre. La rejouer avec ce jeton ferait
 * agir la seconde personne à la place de la première. On ne réutilise donc un
 * jeton neuf que pour la MÊME personne.
 *
 * Aucune vérification de signature ici, et ce n'est pas le but : le serveur
 * vérifie le jeton rejoué. Toute lecture ratée rend `null`, ce qui interdit la
 * réutilisation et ramène au renouvellement normal : le sens sûr de l'échec.
 */
function jwtSub(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload: unknown = JSON.parse(atob(padded));
    if (!payload || typeof payload !== 'object') return null;
    const sub = (payload as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}

/**
 * Un corps qu'on peut renvoyer tel quel. Un flux (`ReadableStream`) est
 * consommé par le premier envoi : le rejouer partirait vide, ce qui serait
 * pire qu'un 401 honnête.
 */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body === undefined || body === null) return true;
  if (typeof body === 'string') return true;
  if (body instanceof URLSearchParams || body instanceof Blob) return true;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  return false;
}

/**
 * La clé du garde-fou : méthode + adresse sans chaîne de requête. Pour une
 * edge, c'est la fonction ; pour PostgREST, la table ou la RPC.
 *
 * La méthode vient de `init` en priorité, mais un `input` déjà en `Request`
 * porte la sienne : sans ce repli, un `POST` passé en `Request` SANS
 * `init.method` retomberait sur `GET` et rejoindrait à tort la clé des
 * lectures de la même adresse.
 */
function refusalKey(input: RequestInfo | URL, init: RequestInit | undefined): string {
  const method = (
    init?.method ?? (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
  ).toUpperCase();
  const url = urlOf(input);
  try {
    const parsed = new URL(url);
    return `${method} ${parsed.origin}${parsed.pathname}`;
  } catch {
    return `${method} ${url.split('?')[0]}`;
  }
}

/**
 * Le marqueur que la porte pose sur sa réponse de refus.
 *
 * POURQUOI UN EN-TÊTE ET NON UN CHAMP DU CORPS
 * --------------------------------------------
 * Sur la jambe edge function, `functions-js` emballe la réponse dans un
 * `FunctionsHttpError` dont `.context` EST la `Response`
 * (`FunctionsClient.ts:161`). Son corps ne se lit qu'en ASYNCHRONE et une
 * seule fois, alors que `errorToast` est synchrone et ne doit pas priver le
 * point d'appel de la lecture du corps. `headers.get()` est synchrone et
 * relisible autant de fois qu'on veut.
 */
export const SESSION_MISSING_HEADER = 'X-Flowcut-Session-Missing';

/**
 * La réponse rendue quand la session est définitivement perdue.
 *
 * Une `Response` 401 plutôt qu'une exception : `postgrest-js` construit son
 * erreur depuis le CORPS de la réponse (`error = JSON.parse(body)`,
 * `PostgrestBuilder.ts:182`), donc le `code: 'PGRST301'` planté ici est
 * exactement ce que `isAuthSessionError` (`src/lib/authErrorRecovery.ts`)
 * reconnaît. Le message « session expirée » et la seconde chance de
 * `queryClient.ts` continuent de fonctionner sans une ligne de modification
 * en aval.
 *
 * ⚠️ Ce n'est PAS par un `status` que la reconnaissance passe : un
 * `PostgrestError` n'en porte jamais, le `status` vit sur le RÉSULTAT
 * (`PostgrestBuilder.ts:218`), pas sur l'erreur. Une exception, elle,
 * traverserait `postgrest-js` sous une forme que rien ne sait interpréter.
 */
export function sessionMissingResponse(): Response {
  return new Response(
    JSON.stringify({
      code: 'PGRST301',
      message: 'session_missing',
      details: 'Requête retenue par la porte : aucune session vivante.',
      hint: null,
    }),
    {
      status: 401,
      headers: { 'Content-Type': 'application/json', [SESSION_MISSING_HEADER]: '1' },
    },
  );
}

/**
 * Cette erreur vient-elle de la porte ?
 *
 * Synchrone, et ne consomme pas le corps de la réponse : `errorToast` en a
 * besoin dans les deux cas. `headers.get()` est insensible à la casse par
 * spécification, donc le nom se passe tel quel.
 *
 * ⛔ Ne PAS remplacer par un test sur le statut 401. Un 401 émis par la
 * fonction elle même pour un vrai refus de droits ne doit PAS s'afficher
 * « session expirée » : c'est la même famille que l'interdit du `42501`.
 */
export function isSessionMissingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // Jambe des fichiers : `storage-js` lit le CORPS de la réponse et construit
  // une `StorageApiError(message, status, statusCode)` sans `.context`
  // (`storage-js/src/lib/fetch.ts:33-37`). Le 401 suffit à la distinguer d'un
  // vrai refus : le serveur de fichiers ne rend jamais 401, il rend 400 avec
  // `"statusCode":"403"`. Le message doit être celui que la porte a planté.
  const storage = error as { name?: unknown; status?: unknown; message?: unknown };
  if (storage.name === 'StorageApiError') {
    return storage.status === 401 && storage.message === 'session_missing';
  }
  // Jambe PostgREST : `postgrest-js` relit le CORPS de la réponse de la porte
  // (`PostgrestBuilder.ts:182`) et rend `{ code, message, details, hint }`,
  // sans `.context`. La paire exacte plantée par `sessionMissingResponse`
  // suffit : un vrai `PGRST301` du serveur porte un autre message
  // (« JWT expired »), et n'est pas une retenue de la porte.
  const rest = error as { code?: unknown; message?: unknown };
  if (rest.code === 'PGRST301' && rest.message === 'session_missing') return true;
  const ctx = (error as { context?: unknown }).context;
  if (!ctx || typeof ctx !== 'object') return false;
  const getter = (ctx as { headers?: { get?: unknown } }).headers?.get;
  if (typeof getter !== 'function') return false;
  try {
    return (ctx as { headers: Headers }).headers.get(SESSION_MISSING_HEADER) === '1';
  } catch {
    // Le sens sûr de l'échec est « ce n'est pas nous » : mieux vaut un message
    // générique en trop qu'un silence qui laisse l'écran vide.
    return false;
  }
}

/**
 * Garde la marque « session perdue » quand un point d'appel reformule son
 * erreur (`throw new Error(message)`, erreur maison). Sans elle, `errorToast`
 * affiche un message générique là où la personne doit se reconnecter.
 *
 * La cible ES2020 exclut `new Error(message, { cause })` : on recopie donc la
 * `Response` de la porte (`.context`), que `isSessionMissingError` lit déjà.
 * Une erreur non marquée est rendue intacte. Ne lève jamais.
 *
 * Une erreur sans `.context` (PostgREST, fichiers) reçoit la paire `PGRST301`
 * + `session_missing`, que `isSessionMissingError` reconnaît.
 */
export function keepSessionMarker<T extends object>(target: T, source: unknown): T {
  if (!isSessionMissingError(source)) return target;
  try {
    const ctx = (source as { context?: unknown }).context;
    if (ctx !== undefined) {
      (target as { context?: unknown }).context = ctx;
    } else {
      // Erreur PostgREST ou de fichiers : la marque vit dans le corps, pas
      // dans `.context`. On pose la paire exacte que `isSessionMissingError`
      // reconnaît sur sa jambe PostgREST (2026-10-02).
      (target as { code?: unknown }).code = 'PGRST301';
      (target as { message?: unknown }).message = 'session_missing';
    }
  } catch {
    // Objet gelé ou propriété en lecture seule : on rend la cible telle quelle.
  }
  return target;
}

/**
 * Le marqueur de la retenue SILENCIEUSE : page privée, aucune session attendue.
 * Distinct de `SESSION_MISSING_HEADER`, qui annonce « session expirée ».
 */
export const SESSION_ABSENT_HEADER = 'X-Flowcut-Session-Absent';

/**
 * La réponse rendue quand une page privée émet une requête anon alors que
 * l'app ne se croit pas connectée : un écran en train de se démonter
 * (déconnexion volontaire, session perdue déjà annoncée, redirection de
 * `ProtectedRoute`). Aucun message n'y est vrai : `errorToast` se tait.
 *
 * 403 et non 401, volontairement : `isAuthSessionError` reconnaît tout
 * `status === 401`, et `storage-js` pose le statut HTTP sur son erreur. Un 401
 * lancerait le rattrapage de `queryClient.ts` et son « session expirée » chez
 * quelqu'un qui vient de choisir de partir.
 *
 * Le corps porte `code` (lu par `postgrest-js`), `message` et `statusCode`
 * (lus par `storage-js`) : chaque bibliothèque retrouve la marque dans SA
 * forme d'erreur, voir `isSessionAbsentError`.
 */
export function sessionAbsentResponse(): Response {
  return new Response(
    JSON.stringify({
      code: 'SESSION_ABSENT',
      message: 'session_absent',
      statusCode: 'SESSION_ABSENT',
      details: 'Requête retenue par la porte : page privée sans session.',
      hint: null,
    }),
    {
      status: 403,
      headers: { 'Content-Type': 'application/json', [SESSION_ABSENT_HEADER]: '1' },
    },
  );
}

/**
 * Cette erreur vient-elle de la retenue silencieuse ? Trois formes, une par
 * bibliothèque : l'en-tête sur `.context` (edge, `FunctionsHttpError`), le
 * `code` (PostgREST, corps relu), le `message` d'une `StorageApiError`.
 * Synchrone ; toute lecture ratée rend `false`, donc un message plutôt qu'un
 * silence.
 */
export function isSessionAbsentError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: unknown; name?: unknown; message?: unknown; context?: unknown };
  if (err.code === 'SESSION_ABSENT') return true;
  if (err.name === 'StorageApiError' && err.message === 'session_absent') return true;
  const ctx = err.context;
  if (!ctx || typeof ctx !== 'object') return false;
  const getter = (ctx as { headers?: { get?: unknown } }).headers?.get;
  if (typeof getter !== 'function') return false;
  try {
    return (ctx as { headers: Headers }).headers.get(SESSION_ABSENT_HEADER) === '1';
  } catch {
    return false;
  }
}

export function createSessionGuardedFetch(deps: SessionGuardDeps): typeof fetch {
  const {
    anonKey,
    baseFetch,
    isSessionExpected,
    isAnonymousDataRoute = () => true,
    isLogoutInProgress = () => false,
    refreshSession,
    now = Date.now,
  } = deps;

  // Un seul renouvellement pour toute une rafale. Sans ce verrou, les 190
  // requêtes d'un montage d'écran en déclencheraient 190.
  let pendingRefresh: Promise<string | null> | null = null;
  // Le dernier jeton neuf obtenu par la porte, à qui il appartient et quand.
  // Sert aux 401 tardifs de la même rafale (voir `FRESH_TOKEN_REUSE_MS`).
  let lastFresh: { jwt: string; sub: string | null; at: number } | null = null;
  const refreshOnce = (): Promise<string | null> => {
    if (!pendingRefresh) {
      // `Promise.resolve().then(...)` plutôt qu'un appel direct : si
      // `refreshSession` levait de façon SYNCHRONE (avant même de rendre une
      // promesse), l'exception s'échapperait d'ici, hors de portée du `.catch`
      // ci-dessous. Même défaut que celui déjà corrigé pour un rejet
      // asynchrone. Inatteignable aujourd'hui (le seul renouvellement
      // enregistré est une fonction `async`), mais une ligne suffit à s'en
      // prémunir.
      pendingRefresh = Promise.resolve()
        .then(() => refreshSession())
        .then((jwt) => {
          // Renouvellement perdu : plus aucun jeton à réutiliser.
          lastFresh = jwt ? { jwt, sub: jwtSub(jwt), at: now() } : null;
          return jwt;
        })
        // Un renouvellement qui LÈVE est un renouvellement perdu. Sans ce
        // `catch`, l'exception s'échappe de la porte, `postgrest-js` la rend
        // en `{ code: "", status: 0 }`, et `isAuthSessionError` ne la
        // reconnaît pas : ni message, ni rejeu. C'est le symptôme d'origine
        // réintroduit par la branche d'échec.
        .catch(() => {
          lastFresh = null;
          return null;
        })
        .finally(() => {
          pendingRefresh = null;
        });
    }
    return pendingRefresh;
  };

  // Le retour, et non plus seulement le départ (spec du 2026-09-26). Une
  // requête partie avec le jeton d'une personne peut être refusée en route :
  // jeton périmé entre le départ et l'arrivée, session fermée sur un autre
  // appareil, mot de passe changé. Côté serveur, tous les 401 de nos fonctions
  // sont des refus d'IDENTITÉ émis AVANT toute action, et PostgREST refuse un
  // jeton avant d'exécuter quoi que ce soit : rejouer ne double donc rien.
  //
  // Adresse → instant jusqu'auquel on ne renouvelle plus. En mémoire
  // seulement : un rechargement la vide, ce qui est voulu.
  const refusedUntil = new Map<string, number>();

  // Renouvellement perdu : le 401 marqué, et le message unique « session
  // expirée » par le chemin existant. Renouvellement réussi : UN rejeu, dont
  // la réponse est rendue telle quelle. Un second 401 est alors un vrai refus,
  // que l'écran doit afficher avec son propre message.
  //
  // Trois garde-fous : un corps en flux n'est pas rejoué, une déconnexion
  // volontaire en cours rend le 401 tel quel, et une adresse qui refuse encore
  // après renouvellement est laissée tranquille pendant `REFUSAL_COOLDOWN_MS`.
  const sendWithPersonJwt = async (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    authorization: string | null,
  ): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (response.status !== 401) return response;
    if (!isPersonJwt(authorization, anonKey)) return response;
    if (!isReplayableBody(init?.body)) return response;
    // Un `input` déjà en `Request` porte SON corps, pas celui d'`init` : ce
    // corps a été consommé par le premier envoi, et le rejouer lèverait au
    // lieu de rendre le 401 honnête.
    if (typeof Request !== 'undefined' && input instanceof Request && input.body !== null) return response;

    // Déconnexion volontaire en cours : `signOut` marque d'abord la session
    // absente, puis la révoque côté serveur. Un appel parti juste avant peut
    // revenir en 401 ; renouveler échouerait et afficherait « session
    // expirée » à quelqu'un qui vient de choisir de partir. Même garde qu'au
    // départ.
    if (!isSessionExpected()) return response;

    const key = refusalKey(input, init);
    const until = refusedUntil.get(key);
    if (until !== undefined && now() < until) return response;

    // 401 tardif de la même rafale : la requête est partie avec l'ancien
    // jeton, et le renouvellement est déjà fini. On rejoue avec le jeton neuf
    // sans en redemander un, À TROIS CONDITIONS : aucun renouvellement en
    // cours (sinon on le rejoint), le jeton neuf a moins de
    // `FRESH_TOKEN_REUSE_MS`, et il appartient à la MÊME personne (voir
    // `jwtSub`). Si c'est le jeton neuf lui-même qui est refusé, on ne le
    // réutilise pas : chemin normal.
    const refused = (authorization ?? '').slice('Bearer '.length);
    const reusable =
      !pendingRefresh &&
      lastFresh !== null &&
      lastFresh.jwt !== refused &&
      now() - lastFresh.at < FRESH_TOKEN_REUSE_MS &&
      lastFresh.sub !== null &&
      jwtSub(refused) === lastFresh.sub;

    const jwt = reusable && lastFresh ? lastFresh.jwt : await refreshOnce();
    if (!jwt) return sessionMissingResponse();

    // Le renouvellement rend la session ACTUELLE de l'appareil, pas forcément
    // celle de la personne au nom de qui la requête est partie : A se
    // déconnecte, B se connecte, le 401 de A revient. Un changement de compte
    // rapide ne doit jamais faire exécuter la requête d'une personne sous
    // l'identité d'une autre. On rend donc le 401 d'origine, sans marque (ce
    // n'est pas une session perdue) et sans garde-fou (ce n'est pas la
    // fonction qui refuse cette personne). Un jeton illisible d'un côté ou de
    // l'autre garde le rejeu : rien ne permet alors d'affirmer un changement.
    const refusedSub = jwtSub(refused);
    const freshSub = jwtSub(jwt);
    if (refusedSub !== null && freshSub !== null && refusedSub !== freshSub) return response;

    const replay = await baseFetch(input, restamp(init, jwt));
    // Refusé AVEC un jeton neuf : ce n'est pas la session, c'est la fonction
    // qui ne reconnaît pas cette personne. Seul un 401 pose le garde-fou.
    if (replay.status === 401) refusedUntil.set(key, now() + REFUSAL_COOLDOWN_MS);
    else refusedUntil.delete(key);
    return replay;
  };

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = urlOf(input);
    if (!GUARDED_PATHS.some((chemin) => url.includes(chemin))) return baseFetch(input, init);

    const authorization = readAuthorization(init);
    if (authorization !== `Bearer ${anonKey}`) {
      if (!RETURN_REPLAY_PATHS.some((chemin) => url.includes(chemin))) return baseFetch(input, init);
      return sendWithPersonJwt(input, init, authorization);
    }

    // Aucune session attendue. Sur une page publique, c'est un visiteur
    // anonyme légitime (tunnel `/book`) : la requête passe. Sur une page
    // privée, c'est un écran qui se démonte : on la retient sans renouveler
    // (l'app sait déjà qu'elle n'est pas connectée) et sans rien envoyer (ce
    // serait un `42501` de plus dans les journaux). Reprise du travail du
    // 2026-09-22, rendue silencieuse le 2026-09-26.
    if (!isSessionExpected()) {
      // Déconnexion volontaire en cours (spec du 2026-09-27) : une lecture
      // lancée juste avant le clic a attendu le verrou d'auth et part
      // maintenant sans session, souvent depuis `/`, page publique. Aucune
      // n'est légitime tant que `signOut` n'a pas fini : retenue silencieuse
      // quelle que soit la page.
      if (isLogoutInProgress()) return sessionAbsentResponse();
      return isAnonymousDataRoute() ? baseFetch(input, init) : sessionAbsentResponse();
    }

    // Ici, et seulement ici : l'app se croit connectée et la requête s'apprête
    // à partir avec la clé anon. C'est exactement le bug.
    const jwt = await refreshOnce();
    if (!jwt) return sessionMissingResponse();

    // Déjà renouvelée au départ : si elle revient en 401, on la rend telle
    // quelle. Un second renouvellement coup sur coup n'apprendrait rien.
    return baseFetch(input, restamp(init, jwt));
  };
}

// --- Enregistrement différé du renouvellement -------------------------------
//
// La porte est construite dans `client.ts`, mais le renouvellement a besoin du
// client Supabase. Sans ce détour, l'import boucle. `AuthContext` enregistre la
// vraie fonction au démarrage ; d'ici là le repli rend `null`, ce qui fait
// bloquer la porte sans tenter de renouveler. C'est le comportement sûr.

let registeredRefresher: (() => Promise<string | null>) | null = null;

export function registerSessionRefresher(fn: () => Promise<string | null>): void {
  registeredRefresher = fn;
}

export async function runRegisteredRefresher(): Promise<string | null> {
  return registeredRefresher ? registeredRefresher() : null;
}

export function __resetSessionRefresherForTests(): void {
  registeredRefresher = null;
}
